import {Injectable} from '@angular/core';
import {TransactionTypeDataService} from '@app/checkout/services/transaction-type-data.service';
import {QuantityIncrementDirective} from '@app/directives/validators/quantity-match-increment.directive';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {DcpPriceData, ProductQuantity} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {BehaviorSubject} from 'rxjs';
import {first, take} from 'rxjs/operators';

export class SpendAmountConfig implements FormlyFieldConfig {
  key = 'spendAmount';
  type = 'quantity';
  defaultValue: number;
  templateOptions = {
    label: 'Spend Amount',
    required: true,
    min: 1,
    max: 9999999999999,
    pattern: '^-?[0-9]+([0-9]+)*$',
    step: 1,
  };
  validators?: {};
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ProductConfigurationService {

  productConfigurationSubject: BehaviorSubject<FormlyFieldConfig[]> = new BehaviorSubject<FormlyFieldConfig[]>([]);

  constructor(
    private transactionTypeService: TransactionTypeService,
    private transactionTypeDataService: TransactionTypeDataService,
    private dcpCartDecoratorService: DcpCartDecoratorService,
  ) {
  }

  setUpConfiguration(orderEntry?: OrderEntry, priceData?: DcpPriceData): void {
    this.dcpCartDecoratorService.isProductCpea().pipe(take(1), untilDestroyed(this)).subscribe(isProductCpea => {
      if (!isProductCpea) {
        this.transactionTypeService.getTransactionTypeByUrlOrOrderEntry(orderEntry).pipe(first(), untilDestroyed(this)).subscribe(transactionType => {
          this.productConfigurationSubject.next(this.transactionTypeDataService.getFormlyFieldConfig(transactionType?.data));
        });
      } else {
        const productQuantityData: ProductQuantity = {};
        let minSpendAmount;
        if (Number(priceData?.price.minimumSpendAmount) > 0) {
          minSpendAmount = Number(priceData.price.minimumSpendAmount);
        } else {
          minSpendAmount = 1;
        }
        productQuantityData.minQuantity = minSpendAmount;
        productQuantityData.defaultValue = minSpendAmount;

        this.setUpSpendAmountConfiguration(orderEntry, productQuantityData);
      }

    });
  }

  setUpSpendAmountConfiguration(orderEntry?: OrderEntry, productQuantityData?: ProductQuantity) {
    this.transactionTypeService.getTransactionTypeByUrlOrOrderEntry(orderEntry).pipe(first(), untilDestroyed(this)).subscribe(tt => {
      let productConfigurationData: FormlyFieldConfig[];
      productConfigurationData = this.transactionTypeDataService.getFormlyFieldConfig(tt?.data);

      if (productQuantityData) {
        productConfigurationData = this.adjustSpendAmountConfigurationData(productConfigurationData, productQuantityData);
      }
      this.productConfigurationSubject.next(productConfigurationData);
    });
  }

  adjustSpendAmountConfigurationData(productConfigData: FormlyFieldConfig[] , productQuantityData: ProductQuantity): FormlyFieldConfig[] {
    if (!productQuantityData || !productConfigData) {
      return;
    }
    const productSpendAmountConfig = new SpendAmountConfig();
    productSpendAmountConfig.defaultValue = productQuantityData.defaultValue;
    productSpendAmountConfig.templateOptions.min = productQuantityData.minQuantity;
    productSpendAmountConfig.templateOptions.max = productQuantityData.maxQuantity;
    productSpendAmountConfig.templateOptions.step = productQuantityData.quantityIncrement;
    productSpendAmountConfig.validators = {
      validation: [QuantityIncrementDirective.quantityIncrementValidator]
    };
    const productQuantityConfig = productConfigData.find(config => config.key === 'quantity');
    productQuantityConfig.hideExpression = true; // this hides the `quantity` input in the UI so that only the `spendAmount` input field is shown
    productConfigData.unshift(productSpendAmountConfig); // quantity objects must be added to the beginning of the FormlyFieldConfig[] for them to be parsed correctly
    return productConfigData;
  }
}
